<template>
  <BaseDialog type="basic" :title="title" :is-open="isOpen" @close="emits('close')">
    <div class="mb-2 flex w-full justify-end px-1">
      <PerspectiveLegend :all-perspectives="topicPerspectives" />
    </div>
    <ul v-if="allQuestions.length > 0" class="flex flex-col gap-0.5">
      <li
        v-for="question in allQuestions"
        :key="question.question.id"
        class="first:*:rounded-t-large! last:*:rounded-b-large!"
      >
        <SelectableQuestion
          :scale="scale"
          :question="{
            questionId: question.question.id,
            content: question.question.content,
            perspectives: question.perspectives,
          }"
          :topic-perspectives="topicPerspectives"
          :is-selected="isQuestionSelected(question.question.id)"
          @click="toggleQuestion(question.question.id)"
        />
      </li>
    </ul>
    <div v-else class="mb-4 flex flex-col items-center justify-center">
      <EmptyQuestions hight-contrast />
      <h4 class="text-title-large text-on-surface text-center">Keine Daten verfügbar</h4>
      <p class="text-body-medium text-on-surface-variant max-w-md text-center">
        Es liegen aktuell keine passenden Daten vor. Bitte wähle eine andere Umfrage, hole neues
        Feedback ein oder lass dieses Feld leer.
      </p>
    </div>
    <template #dismiss>
      <BaseTextButton @click="emits('close')" size="M">{{ $t('common.cancel') }}</BaseTextButton>
    </template>
    <template #confirm>
      <BaseTextButton @click="selectQuestions" size="M" color="tonalPrimary"
        >Auswählen ({{ selectedQuestionIds.size }})</BaseTextButton
      >
    </template>
  </BaseDialog>
</template>
<script lang="ts" setup>
  import { ref, watch } from 'vue'
  import BaseDialog from '../../../core/components/base/dialog/BaseDialog.vue'
  import SelectableQuestion from './SelectableQuestion.vue'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import type { QuestionAnalysis } from '@/core/models/surveys-analytics'
  import type { Perspective } from '@/core/models/perspective'
  import EmptyQuestions from '@/core/components/analytics/question-list/EmptyQuestions.vue'
  import PerspectiveLegend from '@/core/components/analytics/question-list/PerspectiveLegend.vue'

  interface Props {
    title: string
    allQuestions: ReadonlyArray<QuestionAnalysis>
    topicPerspectives: ReadonlyArray<Perspective>
    isOpen: boolean
    scale: ReadonlyArray<{ value: number; label: string }>
    initialQuestionIds: string[]
  }
  const props = defineProps<Props>()

  const selectedQuestionIds = ref(new Set(props.initialQuestionIds))

  watch(
    () => props.initialQuestionIds,
    () => {
      selectedQuestionIds.value.clear()
      for (const questiondId of props.initialQuestionIds) {
        selectedQuestionIds.value.add(questiondId)
      }
    },
  )

  const emits = defineEmits<{
    (e: 'close'): void
    (e: 'questionsSelected', questionId: string[]): void
  }>()

  const isQuestionSelected = (questionId: string) => {
    return selectedQuestionIds.value.has(questionId)
  }
  const toggleQuestion = (questionId: string) => {
    if (selectedQuestionIds.value.has(questionId)) {
      selectedQuestionIds.value.delete(questionId)
    } else {
      selectedQuestionIds.value.add(questionId)
    }
  }

  const selectQuestions = () => {
    emits('questionsSelected', [...selectedQuestionIds.value])
    emits('close')
  }
</script>
